import { Box, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import {
  useSimulationSimulationJobGetAssetClassResultSummaryListQuery,
  useSimulationSimulationJobGetFiftyPercentileListQuery,
} from "@app/api/carApi.generated";
import { BaseDialog } from "@app/components/BaseDialog";
import {
  CarInfoCell,
  CarInfoHeader,
  CarInfoTable,
} from "@app/components/InfoTable";
import { Fragment } from "react";
import { formatPercentFactor, isOddEven } from "@app/utils";
import { CarPortfolioAssetTableData } from "./usePortfolioGroupVersionData";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";

interface TableRow {
  id: string;
  assetClassName: string;
  oneYearPotentialLoss: number;
  weight: number;
  weightedOneYearPotentialLoss: number;
}

interface PortfolioGroupPotentialOneYearLossDialogProps {
  dataId: string;
  moduleId: string;
  tableData: CarPortfolioAssetTableData;
  simulationJobId: string;

  onApply: () => void;
  onCancel: () => void;
}

export const PortfolioGroupPotentialOneYearLossDialog = (
  props: PortfolioGroupPotentialOneYearLossDialogProps,
) => {
  const data = useSimulationSimulationJobGetAssetClassResultSummaryListQuery({
    id: props.simulationJobId,
  });

  const items: TableRow[] = props.tableData.assetRows.level4
    .map((i) => {
      const weight =
        i.modules
          .find((md) => md.moduleId === props.moduleId)
          ?.values.find((v) => v.dataId === props.dataId)?.allocation ?? 0;

      const oneYearPotentialLoss =
        data.data?.find((d) => d.asset_class4 === i.id)
          ?.one_year_downside_nominal_return ?? 0;
      const row: TableRow = {
        id: i.id,
        assetClassName: i.name,
        oneYearPotentialLoss,
        weight,
        weightedOneYearPotentialLoss: oneYearPotentialLoss * weight,
      };
      return row;
    })
    .filter((i) => i.weight > 0);

  const band = props.tableData.bands.find((i) => i.moduleId === props.moduleId);
  const column = band?.columns.find((i) => i.dataId === props.dataId);

  return (
    <BaseDialog
      sx={{ width: "1200px", maxWidth: "100vw" }}
      onClose={props.onCancel}
    >
      <TablePlaceHolder isLoading={data.isLoading} rows={8}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <Typography variant="h2SSemiBold">
            {`Simulation: 1 Year Potential Loss of ${column?.label}`}
            <CarTooltipBox
              sx={{ display: "inline-block", ml: 1, mb: -0.25 }}
              className={pendoClasses.editPortfolioPotentialOneYearLoss}
            />
          </Typography>
          <Typography variant="par02SemiBold" sx={{ mt: 5, fontWeight: 700 }}>
            {band?.label}
          </Typography>
          <CarInfoTable
            sx={{ mt: 1, gridTemplateColumns: "1.5fr 1fr 1fr 1fr" }}
          >
            <CarInfoHeader text="" />
            <CarInfoHeader text="1 Year Potential Loss" />
            <CarInfoHeader text="Weight" />
            <CarInfoHeader text="Weighted 1 Year Potential Loss" />
            {items.map((i, idx) => (
              <Fragment key={i.id}>
                <CarInfoCell
                  text={i.assetClassName}
                  justifyContent="start"
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.oneYearPotentialLoss, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.weight, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.weightedOneYearPotentialLoss, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
              </Fragment>
            ))}
          </CarInfoTable>
        </Box>
      </TablePlaceHolder>
    </BaseDialog>
  );
};
